import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Check,
  Minus,
  MessageSquare,
  Shield,
  Layers,
  AlertCircle,
  Pencil,
  PauseCircle,
  PlayCircle,
  Loader2,
  Trash2,
} from "lucide-react";

const PROVIDER_STYLES = {
  gupshup: { label: "Gupshup", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  twilio: { label: "Twilio", className: "bg-blue-100 text-blue-700 border-blue-200" },
  evolution: { label: "Evolution", className: "bg-orange-100 text-orange-700 border-orange-200" },
  meta_cloud: { label: "Meta Cloud", className: "bg-purple-100 text-purple-700 border-purple-200" },
};

const SCOPE_LABELS = {
  global: "Global",
  secondary: "Secundaria",
  development: "Desarrollo",
  product: "Producto",
};

const STATUS_STYLES = {
  active: { label: "Activa", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  inactive: { label: "Inactiva", className: "bg-gray-100 text-gray-600 border-gray-200" },
  error: { label: "Error", className: "bg-red-100 text-red-700 border-red-200" },
};

const MODULE_KEYS = [
  { key: "notificaciones", letter: "N", label: "Notificaciones" },
  { key: "logicas", letter: "L", label: "Lógicas" },
  { key: "campanas", letter: "C", label: "Campañas" },
  { key: "agente_ia", letter: "A", label: "Agente IA" },
];


function ProviderBadge({ provider }) {
  const cfg = PROVIDER_STYLES[provider] || {
    label: provider || "—",
    className: "bg-gray-100 text-gray-700 border-gray-200",
  };
  return (
    <Badge variant="outline" className={`font-medium ${cfg.className}`} data-testid={`waba-provider-${provider}`}>
      {cfg.label}
    </Badge>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.inactive;
  return (
    <Badge variant="outline" className={`font-medium ${cfg.className}`} data-testid={`waba-status-${status}`}>
      {cfg.label}
    </Badge>
  );
}

function ModulesPills({ modules }) {
  const safe = modules || {};
  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-center gap-1">
        {MODULE_KEYS.map(({ key, letter, label }) => {
          const enabled = !!safe[key];
          return (
            <Tooltip key={key}>
              <TooltipTrigger asChild>
                <span
                  className={[
                    "inline-flex h-6 w-6 items-center justify-center rounded-md border text-[11px] font-semibold transition",
                    enabled
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-transparent text-gray-400 border-gray-300",
                  ].join(" ")}
                  data-testid={`waba-module-${key}-${enabled ? "on" : "off"}`}
                >
                  {letter}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">{label}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

function MetricCard({ icon: Icon, label, value, accent }) {
  return (
    <Card data-testid={`waba-metric-${label}`} className="border border-gray-200 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{value}</p>
          </div>
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${accent}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6" data-testid="waba-loading">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Card key={i} className="border border-gray-200">
            <CardContent className="p-5">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="mt-4 h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="border border-gray-200">
        <CardContent className="p-0">
          <div className="divide-y divide-gray-100">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 p-4">
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-6 w-20" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EmptyState() {
  return (
    <Card className="border border-dashed border-gray-300" data-testid="waba-empty">
      <CardContent className="flex flex-col items-center justify-center gap-3 p-12 text-center">
        <div className="rounded-full bg-gray-100 p-4">
          <MessageSquare className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Aún no hay cuentas WABA</h3>
        <p className="max-w-md text-sm text-gray-500">
          Crea tu primera cuenta WhatsApp Business para empezar a enviar notificaciones,
          campañas y atender leads con tu agente IA.
        </p>
      </CardContent>
    </Card>
  );
}

export default function WabaListView({ refreshKey = 0, onSelectWaba }) {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState("");
  const [togglingId, setTogglingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [editAcc, setEditAcc] = useState(null);
  const [editForm, setEditForm] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setAccounts(null);
    (async () => {
      try {
        const { data } = await api.get("/waba-accounts");
        if (!cancelled) setAccounts(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || "No fue posible cargar las cuentas WABA");
          setAccounts([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  // ── Open edit modal ─────────────────────────────────────────────────
  const openEdit = (acc) => {
    setEditAcc(acc);
    setEditForm({
      name: acc.name || "",
      status: acc.status || "active",
      modules: { ...(acc.modules || {}) },
      is_global_fallback: !!acc.is_global_fallback,
    });
  };
  const closeEdit = () => {
    if (savingId) return;
    setEditAcc(null);
    setEditForm(null);
  };

  const saveEdit = async () => {
    if (!editAcc || !editForm) return;
    if (!editForm.name.trim()) {
      toast.error("El nombre no puede estar vacío");
      return;
    }
    // D3/C1 — advertir SOLO en la transición "tenía respaldo → deja de tenerlo": esta cuenta
    // ERA el fallback y se está apagando. Si ya estaba en false, no hay nada que perder.
    if (editAcc.is_global_fallback === true && !editForm.is_global_fallback) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        "Esta empresa quedaría sin línea de respaldo. Los avisos a vendedores, seguimientos y campañas podrían no enviarse. ¿Continuar de todos modos?"
      );
      if (!ok) return;
    }
    setSavingId(editAcc.id);
    try {
      const payload = {
        ...editAcc,
        name: editForm.name.trim(),
        status: editForm.status,
        modules: { ...editForm.modules },
        // R2-A: el fallback es INDEPENDIENTE del scope (antes se forzaba a false cuando
        // el scope no era global, dejando líneas de producto/desarrollo sin respaldo).
        is_global_fallback: !!editForm.is_global_fallback,
      };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      const { data } = await api.put(`/waba-accounts/${editAcc.id}`, payload);
      setAccounts((prev) => prev.map((a) => (a.id === editAcc.id ? data : a)));
      toast.success("Cuenta actualizada");
      setEditAcc(null);
      setEditForm(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setSavingId("");
    }
  };

  // ── Toggle status ───────────────────────────────────────────────────
  const toggleStatus = async (acc) => {
    const next = acc.status === "active" ? "inactive" : "active";
    setTogglingId(acc.id);
    try {
      // R2-A (A1): NO tocar is_global_fallback al pausar/reactivar. Los resolvers ya
      // filtran por status:"active" (waba_resolver.py:84, campaigns.py:146/160,
      // waba_accounts.py:116) → forzar false no protegía nada y perdía el respaldo sin
      // retorno. El campo viaja tal cual en {...acc} y se preserva; al reactivar se restaura.
      const payload = { ...acc, status: next };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      const { data } = await api.put(`/waba-accounts/${acc.id}`, payload);
      setAccounts((prev) => prev.map((a) => (a.id === acc.id ? data : a)));
      toast.success(next === "active" ? "Cuenta activada" : "Cuenta desactivada");
    } catch (err) {
      // R2-A (A3): al reactivar una línea que conserva el flag de respaldo mientras OTRA
      // activa ya es el fallback, el backend rechaza con 400 (waba_accounts.py:462). Su
      // detail es técnico y habla de "crear otro" (no de reactivar) → mensaje claro para
      // este caso; cualquier otro error conserva el detail del backend.
      if (next === "active" && acc.is_global_fallback === true && err?.response?.status === 400) {
        toast.error("No se puede reactivar: otra línea ya es la de respaldo. Quitale el respaldo a esa línea primero.");
      } else {
        toast.error(err?.response?.data?.detail || "No se pudo cambiar el estado");
      }
    } finally {
      setTogglingId("");
    }
  };

  // ── Hard delete ─────────────────────────────────────────────────────
  const softDelete = async (acc) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      "¿Eliminar permanentemente esta cuenta WABA? Esta acción no se puede deshacer."
    )) return;
    setDeletingId(acc.id);
    try {
      await api.delete(`/waba-accounts/${acc.id}`);
      setAccounts((prev) => prev.filter((a) => a.id !== acc.id));
      toast.success("Cuenta eliminada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    } finally {
      setDeletingId("");
    }
  };

  const metrics = useMemo(() => {
    const list = accounts || [];
    return {
      activas: list.filter((a) => a.status === "active").length,
      // R2-A (A2): una línea inactiva con el flag guardado NO cuenta como respaldo —
      // el resolver no la elegiría (filtra status:"active").
      fallback: list.filter((a) => a.is_global_fallback === true && a.status === "active").length,
      secundarias: list.filter((a) => (a.scope?.type || "") === "secondary").length,
    };
  }, [accounts]);

  if (accounts === null) return <LoadingSkeleton />;

  return (
    <div className="space-y-6" data-testid="waba-list-view">
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <MetricCard
          icon={MessageSquare}
          label="Cuentas activas"
          value={metrics.activas}
          accent="bg-emerald-50 text-emerald-600"
        />
        <MetricCard
          icon={Shield}
          label="Con fallback configurado"
          value={metrics.fallback}
          accent="bg-indigo-50 text-indigo-600"
        />
        <MetricCard
          icon={Layers}
          label="Cuentas secundarias"
          value={metrics.secundarias}
          accent="bg-amber-50 text-amber-600"
        />
      </div>

      {accounts.length === 0 ? (
        <EmptyState />
      ) : (
        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-0">
            <Table data-testid="waba-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Proveedor</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead className="text-center">Fallback</TableHead>
                  <TableHead>Módulos</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map((acc) => {
                  const isSaving = savingId === acc.id;
                  const isToggling = togglingId === acc.id;
                  const isDeleting = deletingId === acc.id;
                  return (
                  <TableRow
                    key={acc.id}
                    data-testid={`waba-row-${acc.id}`}
                    className="cursor-pointer hover:bg-gray-800/50"
                    onClick={() => onSelectWaba?.(acc)}
                  >
                    <TableCell className="font-medium text-gray-100">
                      {acc.name || "—"}
                    </TableCell>
                    <TableCell>
                      <ProviderBadge provider={acc.provider} />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-gray-200">
                      {acc.phone_number || <span className="text-gray-500">—</span>}
                    </TableCell>
                    <TableCell className="text-sm text-gray-300">
                      {SCOPE_LABELS[acc.scope?.type] || acc.scope?.type || "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      {acc.is_global_fallback && acc.status === "active" ? (
                        <Check className="mx-auto h-4 w-4 text-emerald-400" data-testid={`waba-fallback-yes-${acc.id}`} />
                      ) : (
                        <Minus className="mx-auto h-4 w-4 text-gray-500" data-testid={`waba-fallback-no-${acc.id}`} />
                      )}
                    </TableCell>
                    <TableCell>
                      <ModulesPills modules={acc.modules} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={acc.status} />
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <TooltipProvider delayDuration={150}>
                        <div className="flex justify-end gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-gray-300 hover:text-indigo-400"
                                onClick={() => openEdit(acc)}
                                disabled={isSaving}
                                data-testid={`waba-edit-${acc.id}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Editar cuenta</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className={`h-7 w-7 ${
                                  acc.status === "active"
                                    ? "text-amber-400 hover:text-amber-300"
                                    : "text-emerald-400 hover:text-emerald-300"
                                }`}
                                onClick={() => toggleStatus(acc)}
                                disabled={isToggling}
                                data-testid={`waba-toggle-status-${acc.id}`}
                              >
                                {isToggling ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : acc.status === "active" ? (
                                  <PauseCircle className="h-3.5 w-3.5" />
                                ) : (
                                  <PlayCircle className="h-3.5 w-3.5" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {acc.status === "active" ? "Desactivar" : "Activar"}
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-red-400 hover:text-red-300"
                                onClick={() => softDelete(acc)}
                                disabled={isDeleting}
                                data-testid={`waba-delete-${acc.id}`}
                              >
                                {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Eliminar cuenta</TooltipContent>
                          </Tooltip>
                        </div>
                      </TooltipProvider>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ── Edit Modal ─────────────────────────────────────────────── */}
      <Dialog open={!!editAcc} onOpenChange={(v) => !v && closeEdit()}>
        <DialogContent
          className="max-w-lg bg-gray-900 border border-gray-700 text-gray-100"
          onPointerDownOutside={(e) => e.preventDefault()}
          data-testid="waba-edit-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white">Editar cuenta WABA</DialogTitle>
            <DialogDescription className="text-gray-400">
              Proveedor, scope y credenciales no se pueden cambiar — crea una nueva cuenta para eso.
            </DialogDescription>
          </DialogHeader>

          {editForm && editAcc && (
            <div className="space-y-4 py-2">
              {/* Read-only meta — provider + scope no se pueden cambiar */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded bg-gray-800/50 px-3 py-2" data-testid="waba-edit-provider-readonly">
                  <p className="text-xs uppercase text-gray-500">Proveedor</p>
                  <div className="mt-1">
                    <ProviderBadge provider={editAcc.provider} />
                  </div>
                </div>
                <div className="rounded bg-gray-800/50 px-3 py-2" data-testid="waba-edit-scope-readonly">
                  <p className="text-xs uppercase text-gray-500">Scope</p>
                  <p className="mt-1 text-sm text-gray-200">
                    {editAcc.scope?.type === "global" && "Global"}
                    {editAcc.scope?.type === "secondary" && "Secundaria"}
                    {editAcc.scope?.type === "development" &&
                      `Desarrollo${editAcc.scope?.ref_id ? `: ${editAcc.scope.ref_id}` : ""}`}
                    {editAcc.scope?.type === "product" &&
                      `Producto${editAcc.scope?.ref_id ? `: ${editAcc.scope.ref_id}` : ""}`}
                    {!["global", "secondary", "development", "product"].includes(editAcc.scope?.type || "") &&
                      (editAcc.scope?.type || "—")}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-sm text-gray-300">Nombre</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="mt-1 bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-indigo-500"
                  data-testid="waba-edit-name"
                />
              </div>

              <div>
                <Label className="text-sm text-gray-300">Estado</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                >
                  <SelectTrigger
                    className="mt-1 bg-gray-800 border-gray-600 text-white"
                    data-testid="waba-edit-status"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activa</SelectItem>
                    <SelectItem value="inactive">Inactiva</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm text-gray-300">Módulos</Label>
                <div className="mt-2 space-y-2 rounded-md border border-gray-700 bg-gray-800/50 p-3">
                  {[
                    { key: "notificaciones", label: "Notificaciones" },
                    { key: "logicas", label: "Lógicas" },
                    { key: "campanas", label: "Campañas" },
                    { key: "agente_ia", label: "Agente IA" },
                  ].map((m) => (
                    <div
                      key={m.key}
                      className="flex items-center justify-between"
                      data-testid={`waba-edit-module-row-${m.key}`}
                    >
                      <span className="text-sm text-gray-200">{m.label}</span>
                      <Switch
                        checked={!!editForm.modules[m.key]}
                        onCheckedChange={(v) =>
                          setEditForm({
                            ...editForm,
                            modules: { ...editForm.modules, [m.key]: v },
                          })
                        }
                        data-testid={`waba-edit-module-${m.key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-md border border-gray-700 bg-gray-800/50 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-200">Fallback global</p>
                    <p className="text-xs text-gray-400">
                      Esta cuenta se usará cuando no haya una asignación específica para el
                      scope solicitado. Es independiente del scope elegido.
                    </p>
                  </div>
                  <Switch
                    checked={!!editForm.is_global_fallback}
                    onCheckedChange={(v) =>
                      setEditForm({ ...editForm, is_global_fallback: v })
                    }
                    data-testid="waba-edit-fallback"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={closeEdit}
              disabled={!!savingId}
              data-testid="waba-edit-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={saveEdit}
              disabled={!!savingId}
              className="gap-2"
              data-testid="waba-edit-save"
            >
              {savingId && <Loader2 className="h-4 w-4 animate-spin" />}
              {savingId ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
